import { Anchor, Badge, Button, Card, Group, Paper, SimpleGrid, Stack, Text, Title } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DataTable } from 'mantine-datatable';
import type { ReactNode } from 'react';
import { Link, useParams } from 'react-router';
import { rawApi } from '../../api/client';
import { detailKey } from '../../api/queryKeys';
import { type CashPair, MoneyPair, OverShortPair } from '../../components/CashFigures';
import { humanize } from '../../lib/enums';
import { formatDateTime, formatMoney } from '../../lib/format';
import { notifyDone, notifyError } from '../../lib/notify';
import { AdjustedBadge, PullTheDrawer } from './PullTheDrawer';
import { TheDrop } from './TheDrop';

/**
 * One drawer, from the float going in to the shift being signed off.
 *
 * The count history is the point of this screen and it is shown in full, superseded versions
 * included. A drawer that was counted three times before anybody agreed on a number is exactly what
 * somebody looking at a variance needs to see, and hiding the earlier attempts would leave the
 * screen agreeing with a story rather than recording one.
 */

interface Count {
  id: string;
  type: 'opening' | 'closing' | 'adhoc' | 'safe' | 'cajaita';
  version: number;
  supersedesCountId: string | null;
  superseded: boolean;
  counterName: string | null;
  countedAt: string;
  counted: CashPair;
  expected: CashPair | null;
  variance: CashPair | null;
  /** Whether the owner has asked for counts of this kind to be put on his list to look at. */
  verificationRequired: boolean;
  /** The owner, once he has checked this count. A name, never a directory id. */
  checkedByName: string | null;
  checkedAt: string | null;
  /** Whether checking it also moved what the drawer is expected to hold — and why. */
  adjustedTheDrawer: boolean;
  adjustmentReason: string | null;
  note: string | null;
}

interface Session {
  id: string;
  registerName: string;
  ownerName: string | null;
  businessDayId: string;
  status: 'open' | 'closed';
  openedAt: string;
  closedAt: string | null;
  expected: CashPair;
  overShort: CashPair | null;
  counts: Count[];
}

interface Summary {
  /**
   * WHICH OF THE TWO EXPECTED BALANCES IS ON THIS SCREEN, and it is not decoration.
   *
   * `running` is a live figure that moves every time a guest pays, true of `asOf` and of no other
   * moment. `atClose` is what the drawer was expected to be holding when the night finished — on an
   * ordinary night its standard float, because the pouch has gone into the safe by then — and asking
   * again next week gives the same answer. Showing one without saying which leaves the reader
   * assuming the other.
   *
   * Neither of them is what over and short was struck against. That is `countedAgainst` below.
   */
  basis: 'running' | 'atClose';
  asOf: string;
  openingFloat: CashPair;
  paidIn: CashPair;
  paidOut: CashPair;
  transfersIn: CashPair;
  transfersOut: CashPair;
  /** What the owner's audits moved the expectation by. Not trade, and never counted as takings. */
  auditAdjustments: CashPair;
  takings: CashPair;
  /** The same trade as `takings`, read off the notes instead of off the expectation. */
  cashTendered: CashPair;
  changeGiven: CashPair;
  expected: CashPair;
  counted: CashPair | null;
  /**
   * What the closing count was measured against, which is a different moment from `expected`.
   *
   * The drawer is counted, reset out of the float box and dropped into the safe, and only then
   * closed — so the pouch leaves between the count and the end of the shift. `counted` less this is
   * over and short exactly; `counted` less `expected` is over and short plus the whole night's drop,
   * which on a level drawer is a bartender apparently a hundred dollars up.
   */
  countedAgainst: CashPair | null;
  overShort: CashPair | null;
  noSaleCount: number;
  safeDropCount: number;
}

interface DrawerEvent {
  id: string;
  kind: 'paidIn' | 'paidOut' | 'noSale';
  amountMinor: number | null;
  currency: 'USD' | 'HNL' | null;
  reasonCode: string | null;
  reasonName: string | null;
  byName: string | null;
  approvedByName: string | null;
  at: string;
}

/** What each kind of count is for, said the way the person who took it would say it. */
const COUNT_TYPE: Record<Count['type'], string> = {
  opening: 'Float going in',
  closing: 'Counted off',
  adhoc: 'Mid-shift check',
  safe: 'Safe count',
  cajaita: 'Float box count',
};

const EVENT_KIND: Record<DrawerEvent['kind'], string> = {
  paidIn: 'Cash in',
  paidOut: 'Cash out',
  noSale: 'Opened for change',
};

function Figure({ label, value, hint }: { label: string; value: ReactNode; hint?: string }) {
  return (
    <Card withBorder>
      <Text size="xs" c="dimmed">
        {label}
      </Text>
      {value}
      {hint && (
        <Text size="xs" c="dimmed" mt={2}>
          {hint}
        </Text>
      )}
    </Card>
  );
}

export function DrawerSessionPage() {
  const { drawerSessionId = '' } = useParams();
  const queryClient = useQueryClient();

  const session = useQuery({
    queryKey: detailKey('/admin/v1/cash/sessions', drawerSessionId),
    queryFn: () => rawApi<Session>('GET', `/admin/v1/cash/sessions/${drawerSessionId}`),
    enabled: Boolean(drawerSessionId),
  });

  const summary = useQuery({
    queryKey: detailKey('/admin/v1/cash/sessions/summary', drawerSessionId),
    queryFn: () => rawApi<Summary>('GET', `/admin/v1/cash/sessions/${drawerSessionId}/summary`),
    enabled: Boolean(drawerSessionId),
  });

  const events = useQuery({
    queryKey: detailKey('/admin/v1/cash/sessions/events', drawerSessionId),
    queryFn: () => rawApi<DrawerEvent[]>('GET', `/admin/v1/cash/sessions/${drawerSessionId}/events`),
    enabled: Boolean(drawerSessionId),
  });

  const record = session.data;

  /**
   * SIGNING OFF A COUNT SOMEBODY ELSE TOOK — the cheapest audit there is, and the one that clears
   * the list.
   *
   * The column beside it says "Not looked at yet" on every count the owner has asked to be shown.
   * A screen that says so and offers no way to look at them is the defect this repository keeps
   * finding: a control announced on a page, working on the server, and unreachable by the one person
   * it exists for. The refusal that used to sit behind this — a second bartender's PIN — is gone,
   * because there was never a second bartender.
   *
   * Nothing waits on it and nothing is refused for want of it: pressing this records that he read
   * the count against what the drawer was expected to hold, with his name and the time.
   */
  const signOff = useMutation({
    mutationFn: (countId: string) => rawApi('POST', `/admin/v1/cash/counts/${countId}/check`, {}),
    onSuccess: () => {
      notifyDone('Signed off', 'Your name and the time are on that count.');
      queryClient.invalidateQueries({ queryKey: detailKey('/admin/v1/cash/sessions', drawerSessionId) });
    },
    onError: (error) => notifyError(error, 'sign off that count'),
  });

  return (
    <Stack gap="md">
      <div>
        <Anchor component={Link} to="/register" size="sm">
          Back to the register
        </Anchor>
        <Title order={3} mt={4}>
          {record ? record.registerName : 'Drawer'}
        </Title>
        <Text size="sm" c="dimmed">
          {record
            ? `${record.ownerName ?? 'Nobody named'} · opened ${formatDateTime(record.openedAt)}${
                record.closedAt ? ` · counted off ${formatDateTime(record.closedAt)}` : ' · still open'
              }`
            : 'Loading'}
        </Text>
      </div>

      <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }}>
        <Figure
          label="Float it started with"
          value={<MoneyPair pair={summary.data?.openingFloat} fw={700} />}
        />
        <Figure
          label="Taken in trade"
          value={<MoneyPair pair={summary.data?.takings} fw={700} />}
          hint="What is left once the float and every movement that was not a sale are taken out"
        />
        {/*
          THE FIGURE HE CAME HERE FOR, AND THE SENTENCE THAT SAYS WHICH FIGURE IT IS.

          "I should be able to pull the drawer at any time whether we are open or closed and the
          system should report how much is expected in the drawer." There are two honest answers to
          that and they are not the same number:

            - on a drawer still trading it is a RUNNING total, true of the instant it was read and
              different by the time he has walked to the register;
            - on a drawer counted off it is FROZEN at the end of the shift — which on an ordinary
              night is the standard float, because the takings are in the safe by then — and asking
              again next week gives the same answer.

          A card headed "Expected" with no such sentence under it is read as the second when it is
          the first, which is exactly the misreading that makes somebody argue with a bartender
          about a figure that had already moved.

          It is deliberately NOT the figure the count was struck against, and the card beside it says
          so. The night's order is count, reset, pouch, drop, close, so the drop lands between the
          two — subtract this from what was counted and you get over and short plus the entire drop,
          which reads as a bartender a hundred dollars up on the night she balanced exactly.
        */}
        <Figure
          label="Should be in it"
          value={<MoneyPair pair={summary.data?.expected} fw={700} />}
          hint={
            summary.data
              ? summary.data.basis === 'atClose'
                ? `As the shift ended, ${formatDateTime(summary.data.asOf)} — after the drop went into the safe`
                : `Still trading — as it stood at ${formatDateTime(summary.data.asOf)}, and moving`
              : undefined
          }
        />
        <Figure
          label="Over or short"
          value={<OverShortPair pair={summary.data?.overShort} />}
          hint={
            summary.data?.counted && summary.data.countedAgainst
              ? `Counted ${formatMoney(summary.data.counted.usdMinor, 'USD')} and ${formatMoney(summary.data.counted.hnlMinor, 'HNL')} against ${formatMoney(summary.data.countedAgainst.usdMinor, 'USD')} and ${formatMoney(summary.data.countedAgainst.hnlMinor, 'HNL')} owed at that moment. Nobody types this figure in`
              : 'What was counted, less what the drawer was expected to hold at the moment it was counted. Nobody types this figure in'
          }
        />
      </SimpleGrid>

      {/*
        The owner's own count of this drawer, and the whole of what he asked for on a live one:
        record it, leave the shift running, with the option to move the drawer afterwards. It sits
        directly under the expectation because that is the number it is about.
      */}
      {record && (
        <PullTheDrawer
          drawerSessionId={record.id}
          status={record.status}
          onRecorded={() => events.refetch()}
        />
      )}

      {/*
        The same money as "Taken in trade", counted the other way round — off the notes that crossed
        the counter rather than off the drawer's expectation. Two sums, two sets of rows, and they
        have to agree; showing both is what lets somebody notice on the screen when they do not.
      */}
      <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }}>
        <Figure
          label="Guests handed over"
          value={<MoneyPair pair={summary.data?.cashTendered} />}
          hint="Cash only — a card never reaches this drawer"
        />
        <Figure
          label="Change handed back"
          value={<MoneyPair pair={summary.data?.changeGiven} />}
          hint="Handed over, less change back, is what the drawer took in trade"
        />
        <Figure label="Cash in" value={<MoneyPair pair={summary.data?.paidIn} />} />
        <Figure label="Cash out" value={<MoneyPair pair={summary.data?.paidOut} />} />
        {/*
          Shown only when it happened, because on every other drawer it is nought and a card reading
          nought invites the question "should there be something here?" every night for a year.

          It is its own line rather than being folded into the takings for a reason worth stating on
          the screen it appears on: moving the drawer changes what it is expected to hold without a
          single note crossing the counter, so counting it as trade would report money the bar never
          took, on exactly the shift somebody is already asking questions about.
        */}
        {summary.data &&
          (summary.data.auditAdjustments.usdMinor !== 0 || summary.data.auditAdjustments.hnlMinor !== 0) && (
            <Figure
              label="Moved by an audit"
              value={<MoneyPair pair={summary.data.auditAdjustments} />}
              hint="What pulling this drawer moved its expectation by. Not trade, and not counted as takings"
            />
          )}
        <Figure
          label="Moved in"
          value={<MoneyPair pair={summary.data?.transfersIn} />}
          hint="From the float box or the safe"
        />
        <Figure
          label="Dropped or moved out"
          value={<MoneyPair pair={summary.data?.transfersOut} />}
          hint={
            summary.data
              ? `${summary.data.safeDropCount} drop${summary.data.safeDropCount === 1 ? '' : 's'} into the safe · drawer opened for change ${summary.data.noSaleCount} time${summary.data.noSaleCount === 1 ? '' : 's'}`
              : undefined
          }
        />
      </SimpleGrid>

      {/*
        What should have gone into the safe, and what did. It sits between the movements and the
        counts because it is about neither on its own: the drop is what the movements left over, and
        it is measured against the float rather than against any count.
      */}
      {record && <TheDrop drawerSessionId={record.id} status={record.status} />}

      <Paper withBorder p="md" radius="md">
        <Title order={5}>Counts</Title>
        <Text size="sm" c="dimmed" mt={2} mb="sm">
          Every count taken on this drawer, in order. A recount does not replace what the first one said — it
          is a new version, and only the newest one counts towards over and short.
        </Text>
        <DataTable
          withTableBorder
          minHeight={120}
          fetching={session.isLoading}
          records={record?.counts ?? []}
          idAccessor="id"
          noRecordsText="This drawer has not been counted."
          rowBackgroundColor={(row) => (row.superseded ? 'var(--mantine-color-gray-light)' : undefined)}
          columns={[
            {
              accessor: 'type',
              title: 'Count',
              render: (row) => (
                <Group gap={6} wrap="nowrap">
                  <Text size="sm">{COUNT_TYPE[row.type]}</Text>
                  {row.version > 1 && (
                    <Badge size="xs" variant="light">
                      Recount {row.version}
                    </Badge>
                  )}
                  {row.superseded && (
                    <Badge size="xs" color="gray" variant="light">
                      Replaced
                    </Badge>
                  )}
                </Group>
              ),
            },
            { accessor: 'countedAt', title: 'When', render: (row) => formatDateTime(row.countedAt) },
            { accessor: 'counterName', title: 'Counted by', render: (row) => row.counterName ?? '—' },
            {
              /*
               * WHO CHECKED IT, which used to say "Verified by" and meant a second bartender.
               *
               * There is one bartender on a shift, so that column could only ever read "Nobody yet"
               * — a control the screen claimed and nobody in the building could satisfy. What goes
               * here now is the owner's own attestation, made from this site afterwards, which is
               * the check he actually asked for and the only one anybody can perform.
               */
              accessor: 'checkedByName',
              title: 'Checked by',
              render: (row) =>
                row.checkedByName ? (
                  <div>
                    <Text size="sm">{row.checkedByName}</Text>
                    {row.checkedAt && (
                      <Text size="xs" c="dimmed">
                        {formatDateTime(row.checkedAt)}
                      </Text>
                    )}
                  </div>
                ) : row.superseded ? (
                  /* A replaced count no longer posts to over and short, so signing for it would be
                     signing for a figure nobody is using. Sign off the recount instead. */
                  <Text size="sm" c="dimmed">
                    —
                  </Text>
                ) : (
                  <Button
                    size="compact-xs"
                    variant={row.verificationRequired ? 'light' : 'subtle'}
                    loading={signOff.isPending && signOff.variables === row.id}
                    onClick={() => signOff.mutate(row.id)}
                  >
                    {row.verificationRequired ? 'Sign it off' : 'Sign off'}
                  </Button>
                ),
            },
            { accessor: 'counted', title: 'Counted', render: (row) => <MoneyPair pair={row.counted} /> },
            {
              accessor: 'expected',
              title: 'Should have been',
              render: (row) => <MoneyPair pair={row.expected} />,
            },
            {
              /*
               * The difference, and — where the drawer was moved onto this count — the words that
               * justified moving it, in the same cell as the figure they are about. A count that
               * says "the expectation was moved" and cannot say on whose reasoning is the same
               * defect as a signature column that can only print "Yes".
               */
              accessor: 'variance',
              title: 'Difference',
              render: (row) => (
                <Stack gap={2}>
                  <OverShortPair pair={row.variance} />
                  {row.adjustedTheDrawer && <AdjustedBadge reason={row.adjustmentReason} />}
                </Stack>
              ),
            },
            { accessor: 'note', title: 'Note', render: (row) => row.note ?? '' },
          ]}
        />
      </Paper>

      <Paper withBorder p="md" radius="md">
        <Title order={5}>Cash in and out</Title>
        <Text size="sm" c="dimmed" mt={2} mb="sm">
          Money that went into or out of this drawer without being a sale, and every time the drawer was
          opened to break a note.
        </Text>
        <DataTable
          withTableBorder
          minHeight={100}
          fetching={events.isLoading}
          records={events.data ?? []}
          idAccessor="id"
          noRecordsText="Nothing went in or out of this drawer."
          columns={[
            { accessor: 'at', title: 'When', render: (row) => formatDateTime(row.at) },
            { accessor: 'kind', title: 'What', render: (row) => EVENT_KIND[row.kind] },
            {
              accessor: 'amountMinor',
              title: 'Amount',
              render: (row) =>
                row.amountMinor === null || row.currency === null
                  ? '—'
                  : formatMoney(row.amountMinor, row.currency),
            },
            {
              accessor: 'reasonCode',
              title: 'Reason',
              // The name the owner gave the reason, and only a tidied-up form of the stored code
              // where the list has nothing to name it. The raw word never reaches the screen.
              render: (row) => row.reasonName ?? (row.reasonCode ? humanize(row.reasonCode) : '—'),
            },
            { accessor: 'byName', title: 'By', render: (row) => row.byName ?? '—' },
            {
              accessor: 'approvedByName',
              title: 'Approved by',
              render: (row) => row.approvedByName ?? '—',
            },
          ]}
        />
      </Paper>
    </Stack>
  );
}
